import { logStorageIssue } from "./errorLogger";

export type BetaRole = "admin" | "viewer";

export type BetaUser = {
  name?: string;
  email: string;
  role: BetaRole;
};

const BETA_AUTH_KEY = "beta-user";

const safeGetItem = (storage: Storage | undefined, key: string): string | null => {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (storage: Storage | undefined, key: string, value: string): boolean => {
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const safeRemoveItem = (storage: Storage | undefined, key: string): boolean => {
  if (!storage) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

export const getBetaUser = (): BetaUser | null => {
  if (typeof window === "undefined") return null;
  const local = typeof localStorage !== "undefined" ? localStorage : undefined;
  const session = typeof sessionStorage !== "undefined" ? sessionStorage : undefined;
  const storedLocal = safeGetItem(local, BETA_AUTH_KEY);
  const storedSession = storedLocal ? null : safeGetItem(session, BETA_AUTH_KEY);
  const stored = storedLocal ?? storedSession;
  const source = storedLocal ? "localStorage" : storedSession ? "sessionStorage" : null;

  if (!stored) {
    // Missing beta auth is a normal unauthenticated state, not a storage failure.
    return null;
  }

  logStorageIssue("getBetaUser", true, {
    source,
    hasData: true,
  });

  try {
    const parsed = JSON.parse(stored) as BetaUser;
    if (!parsed?.email || !parsed?.role) {
      safeRemoveItem(local, BETA_AUTH_KEY);
      safeRemoveItem(session, BETA_AUTH_KEY);
      return null;
    }
    return parsed;
  } catch {
    safeRemoveItem(local, BETA_AUTH_KEY);
    safeRemoveItem(session, BETA_AUTH_KEY);
    return null;
  }
};

export const setBetaUser = (user: BetaUser | null): void => {
  const local = typeof localStorage !== "undefined" ? localStorage : undefined;
  const session = typeof sessionStorage !== "undefined" ? sessionStorage : undefined;

  if (!user) {
    const clearedLocal = safeRemoveItem(local, BETA_AUTH_KEY);
    const clearedSession = safeRemoveItem(session, BETA_AUTH_KEY);
    logStorageIssue("setBetaUser-clear", true, {
      clearedLocal,
      clearedSession,
    });
    return;
  }

  const json = JSON.stringify(user);
  const wroteLocal = safeSetItem(local, BETA_AUTH_KEY, json);
  const wroteSession = safeSetItem(session, BETA_AUTH_KEY, json);

  const verifyLocal = safeGetItem(local, BETA_AUTH_KEY);
  const verifySession = safeGetItem(session, BETA_AUTH_KEY);
  const verifiedLocal = Boolean(verifyLocal);
  const verifiedSession = Boolean(verifySession);
  const success = wroteLocal || wroteSession;

  logStorageIssue("setBetaUser", success, {
    user: user.email,
    role: user.role,
    wroteLocal,
    wroteSession,
    verifiedLocal,
    verifiedSession,
  });
};

export const isBetaAuthenticated = (): boolean => Boolean(getBetaUser());

export const isBetaAdminAuthenticated = (): boolean => {
  const user = getBetaUser();
  return user?.role === "admin";
};
